import RightArrowIconWhite from "../../../assets/icons/header-icons/right-arrow-white.svg";
import {
  handleDemoLogin,
  loginSuccess,
  useAppDispatch,
  useAppSelector,
  SIGN_UP_ROUTE,
  getSapToken,
  showToast,
  ACCEPT_TERMS_ROUTE,
  RESET_PASSWORD_ROUTE,
  useLogin,
} from "@hypexone/uicore";
import HeaderButtonComponent from "./HeaderButtonComponent";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LoginPageBtn from "../login-page-btn/LoginPageBtn";
import handIcon from "../../../assets/images/hand-right.png";
import instagramIcon from "../../../assets/icons/instagram-icon.svg";
import telegramIcon from "../../../assets/icons/telegram-icon.svg";
import androidIcon from "../../../assets/icons/android-icon.svg";
import downloadIconSvg from "../../../assets/icons/download-icon.svg";
import { isDesktop, isMobile } from "react-device-detect";
import SocialAuth from "../../pages/social-auth/SocialAuth";
import { useSignUpModal } from "../../context/SignUpModalContext";
import { useResendEmailModal } from "../../context/ResendEmailModalContext";
import { useForgotPasswordModal } from "../../context/ForgotPasswordModalContext";
import { domain, isWhatsappSignup } from "../../../constants/Branding";
import { getEnvVariable } from "../../../constants/brandConfig";
import { trackApkDownload } from "../../../utils/trackApkDownload";
import LoginBrandActions from "./LoginBrandActions";

type LoginVariant = "header" | "mobilePage" | "standalone";

const supportEmailId = "laserwin247@gmail.com";

// --- Login2FAContent (shared 2FA + Reset 2FA forms) ---
interface Login2FAContentProps {
  showReset2FA: boolean;
  setShowReset2FA: (v: boolean) => void;
  twoFactorCode: string;
  setTwoFactorCode: (v: string) => void;
  handle2FASubmit: (e: React.FormEvent) => void;
  handle2FABack: () => void;
  is2FALoading: boolean;
  identifierValue: string;
  setIdentifierValue: (v: string) => void;
  resetIdentifierType: "PHONE_NUMBER" | "EMAIL";
  setResetIdentifierType: (v: "PHONE_NUMBER" | "EMAIL") => void;
  resetOtp: string;
  setResetOtp: (v: string) => void;
  resetOtpTimer: number | undefined;
  resetOtpEnterFields: boolean;
  resetLoading: boolean;
  isResetting: boolean;
  resetErrorMsg: string;
  generateResetOtp: () => void;
  resetTwoFactor: () => void;
  handleReset2FABack: () => void;
  langData: Record<string, string> | undefined;
  isInline?: boolean;
}

const formatOTPInput = (value: string) => value.replace(/\D/g, "").slice(0, 6);

const Login2FAContent: React.FC<Login2FAContentProps> = ({
  showReset2FA,
  setShowReset2FA,
  twoFactorCode,
  setTwoFactorCode,
  handle2FASubmit,
  handle2FABack,
  is2FALoading,
  identifierValue,
  setIdentifierValue,
  resetIdentifierType,
  setResetIdentifierType,
  resetOtp,
  setResetOtp,
  resetOtpTimer,
  resetOtpEnterFields,
  resetLoading,
  isResetting,
  resetErrorMsg,
  generateResetOtp,
  resetTwoFactor,
  handleReset2FABack,
  langData,
  isInline = false,
}) => {
  const textClass = isInline ? "text-black4" : "text-white";
  const inputClass = isInline
    ? "w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-black"
    : "w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-black";
  const btnSecondary = isInline
    ? "flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600"
    : "flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700";
  const btnPrimary = isInline
    ? "flex-1 bg-header-gradient text-goldenYellow py-2 px-4 rounded-lg font-bold disabled:opacity-50"
    : "flex-1 bg-goldenYellow text-black py-2 px-4 rounded-md font-semibold hover:bg-yellow-500 disabled:opacity-50";

  if (showReset2FA) {
    return (
      <form onSubmit={(e) => e.preventDefault()} className="w-full">
        <div className="text-center mb-4">
          <h3 className={`text-lg font-semibold ${textClass} mb-1`}>
            {langData?.["reset_two_factor_auth"] ?? "Reset Two-Factor Authentication"}
          </h3>
          <p className={`text-sm ${isInline ? "text-black3" : "text-gray-300"}`}>
            {langData?.["reset_2fa_description"] ??
              "Enter your phone number or email to reset 2FA. You will receive an OTP to verify."}
          </p>
        </div>
        {!resetOtpEnterFields ? (
          <>
            <div className="flex gap-2 mb-3">
              <select
                className={`${inputClass} min-w-fit max-w-fit shrink-0`}
                value={resetIdentifierType}
                onChange={(e) =>
                  setResetIdentifierType(e.target.value === "EMAIL" ? "EMAIL" : "PHONE_NUMBER")
                }
              >
                <option value="PHONE_NUMBER">+91</option>
                <option value="EMAIL">{langData?.["email"] ?? "Email"}</option>
              </select>
              <input
                type="text"
                className={`${inputClass}`}
                value={identifierValue}
                onChange={(e) => setIdentifierValue(e.target.value)}
                placeholder={
                  resetIdentifierType === "EMAIL"
                    ? langData?.["enter_email_txt"] ?? "Enter Email"
                    : langData?.["enter_whatsapp_no"] ?? "Enter Phone Number"
                }
              />
            </div>
            {resetErrorMsg && (
              <div className="text-red-500 text-sm text-center mb-2">{resetErrorMsg}</div>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={handleReset2FABack} className={btnSecondary}>
                {langData?.["back_to_2fa"] ?? "Back to 2FA"}
              </button>
              <button
                type="button"
                onClick={generateResetOtp}
                disabled={!identifierValue.trim() || resetLoading}
                className={`${btnPrimary} disabled:opacity-50`}
              >
                {resetLoading ? (langData?.["sending"] ?? "Sending...") : langData?.["send_otp"] ?? "Send OTP"}
              </button>
            </div>
          </>
        ) : (
          <>
            {resetOtpTimer != null && (
              <div className={`text-center mb-2 text-sm ${isInline ? "text-black4" : "text-gray-300"}`}>
                {langData?.["otp_timer"] ?? "Resend OTP in"} {resetOtpTimer}s
              </div>
            )}
            <div className="mb-3">
              <input
                type="tel"
                inputMode="numeric"
                className={inputClass}
                value={resetOtp}
                onChange={(e) => setResetOtp(formatOTPInput(e.target.value))}
                placeholder={langData?.["enter_otp_txt"] ?? "Enter OTP"}
                maxLength={6}
              />
            </div>
            {resetErrorMsg && (
              <div className="text-red-500 text-sm text-center mb-2">{resetErrorMsg}</div>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={handleReset2FABack} className={btnSecondary}>
                {langData?.["back_to_2fa"] ?? "Back to 2FA"}
              </button>
              <button
                type="button"
                onClick={resetTwoFactor}
                disabled={resetOtp.length !== 6 || isResetting}
                className={`${btnPrimary} disabled:opacity-50`}
              >
                {isResetting ? (langData?.["resetting"] ?? "Resetting...") : langData?.["verify_and_reset_2fa"] ?? "Verify & Reset 2FA"}
              </button>
            </div>
          </>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={handle2FASubmit} className="w-full">
      <div className="text-center mb-4">
        <h3 className={`text-lg font-semibold ${textClass} mb-1`}>
          {langData?.["two_factor_auth"] ?? "Two-Factor Authentication"}
        </h3>
        <p className={`text-sm ${isInline ? "text-black3" : "text-gray-300"}`}>
          {langData?.["enter_6_digit_code"] ?? "Enter the 6-digit code from your authenticator app"}
        </p>
      </div>
      <div className="mb-4">
        <label className={`block text-sm font-normal mb-1 ${textClass}`}>
          {langData?.["authentication_code"] ?? "Authentication Code"}
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={twoFactorCode}
          onChange={(e) => setTwoFactorCode(formatOTPInput(e.target.value))}
          placeholder="123456"
          className={`${inputClass} text-center text-lg tracking-widest`}
          maxLength={6}
          autoComplete="off"
        />
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={handle2FABack} className={btnSecondary}>
          {langData?.["back_to_login"] ?? "Back to Login"}
        </button>
        <button
          type="submit"
          disabled={is2FALoading || twoFactorCode.length !== 6}
          className={`${btnPrimary} disabled:opacity-50`}
        >
          {is2FALoading ? (langData?.["verifying"] ?? "Verifying...") : langData?.["verify_and_login"] ?? "Verify & Login"}
        </button>
      </div>
      <div className="flex justify-center mt-3">
        <button
          type="button"
          onClick={() => setShowReset2FA(true)}
          className={`text-sm hover:underline ${isInline ? "text-red-600" : "text-red-400"}`}
        >
          {langData?.["reset_two_factor_auth"] ?? "Reset Two-Factor Authentication"}
        </button>
      </div>
    </form>
  );
};

// --- LoginFormFields ---
interface LoginFormFieldsProps {
  variant: LoginVariant;
  formValues: { username: string; password: string };
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  validationCode: string;
  validationInput: string;
  setValidationInput: (v: string) => void;
  handleEnterKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleForgotPassClick: () => void;
  handleResendEmailClick: () => void;
  handleSignUpClick: () => void;
  showSignUp: boolean;
  whatsappSignup: boolean;
  langData: Record<string, string> | undefined;
  domainConfig: { b2cEnabled?: boolean };
}

const LoginFormFields: React.FC<LoginFormFieldsProps> = ({
  variant,
  formValues,
  handleChange,
  validationCode,
  validationInput,
  setValidationInput,
  handleEnterKeyPress,
  handleForgotPassClick,
  handleResendEmailClick,
  handleSignUpClick,
  showSignUp,
  whatsappSignup,
  langData,
  domainConfig,
}) => {
  const isMobilePage = variant === "mobilePage";
  const textClass = isMobilePage ? "text-black4" : "text-white";
  const labelClass = isMobilePage ? "text-black3" : "text-white";

  const isHeader = variant === "header";
  return (
    <div
      className={`relative ${
        variant === "mobilePage" ? "flex flex-col w-full items-center space-y-2" : "flex items-center gap-2"
      }`}
    >
      <input
        name="username"
        placeholder="Username"
        className={`px-2 rounded-[5px] md:placeholder-gray-400 placeholder:text-sm text-sm outline-none ${
          isMobilePage ? "h-[40px] w-full bg-white rounded-lg" : "h-[25px] md:w-[130px]"
        }`}
        value={formValues.username}
        onChange={handleChange}
        onKeyDown={handleEnterKeyPress}
      />
      <input
        name="password"
        type="password"
        placeholder="Password"
        className={`px-2 rounded-[5px] md:placeholder-gray-400 placeholder:text-sm text-sm outline-none ${
          isMobilePage ? "h-[40px] w-full bg-white rounded-lg" : "h-[25px] md:w-[130px]"
        }`}
        value={formValues.password}
        onChange={handleChange}
        onKeyDown={handleEnterKeyPress}
      />
      <div
        className={`flex items-center gap-2 px-2 rounded-[5px] bg-white ${
          isMobilePage ? "h-[40px] w-full mb-2 rounded-lg" : "h-[25px] md:w-[130px]"
        }`}
      >
        <input
          id="login-validation-input"
          type="text"
          inputMode="numeric"
          placeholder={langData?.["validation"] ?? "Validation"}
          className="flex-1 min-w-0 bg-transparent placeholder:text-sm text-sm text-black outline-none border-none"
          value={validationInput}
          onChange={(e) => setValidationInput(e.target.value)}
          onKeyDown={handleEnterKeyPress}
          aria-label={langData?.["validation"] ?? "Validation code"}
        />
        <span
          className={
            isHeader
              ? "font-semibold text-black text-sm shrink-0"
              : "font-bold text-black text-sm max-md:text-2xl shrink-0"
          }
        >
          {validationCode}
        </span>
      </div>
      <div
        className={
          isMobilePage
            ? "w-full flex justify-between items-center text-nowrap text-sm mt-2 text-black4 gap-2"
            : isHeader
              ? "flex flex-col items-start gap-x-2 text-nowrap text-[10px]"
              : "w-full flex justify-end text-nowrap text-[10px] flex-col mt-1"
        }
      >
        {showSignUp && isDesktop && !isMobilePage && (
          <div onClick={handleSignUpClick} className={`cursor-pointer ${isHeader ? "" : "mr-1"} ${textClass}`}>
            {whatsappSignup ? langData?.["sign_in"] || "Sign In" : langData?.["sign_up"]}
          </div>
        )}
        {domainConfig.b2cEnabled && (
          <div
            onClick={handleForgotPassClick}
            className={`cursor-pointer w-fit underline ${isHeader ? "text-[10px]" : ""} ${isMobilePage ? "text-sm" : ""} ${isHeader ? "" : "mr-1"} ${textClass}`}
          >
            {langData?.["forgot_password"]}?
          </div>
        )}
        {domainConfig.b2cEnabled && (
          <div
            onClick={handleResendEmailClick}
            className={`cursor-pointer w-fit underline ${isMobilePage ? "text-sm" : "text-[10px]"} ${isHeader ? "" : "mr-1"} ${textClass}`}
          >
            {langData?.["resend_email"] || "Resend Verification email"}?
          </div>
        )}
      </div>
    </div>
  );
};

// --- LoginActionButtons ---
interface LoginActionButtonsProps {
  variant: LoginVariant;
  handleLogin: () => void;
  onDemoLogin: () => void;
  loading: boolean;
  demoLoginLoader: boolean;
  setDemoLoginLoader: (v: boolean) => void;
  langData: Record<string, string> | undefined;
  domainConfig: { demoUser?: boolean };
}

const LoginActionButtons: React.FC<LoginActionButtonsProps> = ({
  variant,
  handleLogin,
  onDemoLogin,
  loading,
  demoLoginLoader,
  setDemoLoginLoader,
  langData,
  domainConfig,
}) => {
  const isMobilePage = variant === "mobilePage";
  const isHeader = variant === "header";
  const loginBtnClass = isMobilePage
    ? "w-full bg-header-gradient text-goldenYellow p-[10px] text-[13px] rounded-lg font-bold hover:bg-black4"
    : undefined;

  const handleDemoClick = () => {
    setDemoLoginLoader(true);
    onDemoLogin();
  };

  if (isHeader) {
    return (
      <div className="flex items-center max-md:space-y-2">
        <div className="flex items-center md:gap-2">
          <HeaderButtonComponent
            rightIcon={{ src: RightArrowIconWhite, alt: "right-arrow" }}
            customCSS={"px-2"}
            onClick={handleLogin}
            isRedButton
            buttonText={"Login"}
            loading={loading}
          />
          {domainConfig.demoUser && (
            <HeaderButtonComponent
              rightIcon={{ src: RightArrowIconWhite, alt: "right-arrow" }}
              customCSS={"px-2"}
              onClick={handleDemoClick}
              isRedButton
              buttonText={isDesktop ? langData?.["demo"] || "Demo": langData?.["demo_login"] || "Demo Login"}
              loading={demoLoginLoader}
            />
          )}
        </div>
        <div className="flex items-center justify-center text-[10px] font-[500]">
          <span className="text-white text-nowrap text-[10px] mx-1">or</span>
          <SocialAuth />
        </div>
      </div>
    );
  }

  return (
    <>
      <LoginPageBtn
        loading={loading}
        onClick={handleLogin}
        label={"Login"}
        className={loginBtnClass}
      />
      <LoginPageBtn
        loading={demoLoginLoader}
        onClick={handleDemoClick}
        label={langData?.["demo_login"] ?? "Login With Demo ID"}
        className={loginBtnClass}
      />
      <div className={`flex flex-col items-center justify-center text-[10px] font-[500] gap-1 w-full ${isMobilePage ? "my-3" : ""}`}>
        {/* <div className={`text-nowrap text-[11px] flex-col ${isMobilePage ? "text-black3" : "text-white"}`}>
          or continue with
        </div> */}
        <SocialAuth />
      </div>
    </>
  );
};

// --- LoginFooter ---
interface LoginFooterProps {
  variant: LoginVariant;
  supportEmailId: string;
  handleSignUpClick: () => void;
  showSignUp: boolean;
  whatsappSignup: boolean;
  langData: Record<string, string> | undefined;
  domainConfig: {
    b2cEnabled?: boolean;
    apkUrl?: string;
    suppportContacts?: Array<{ contactType?: string; details?: string }>;
  };
}

const LoginFooter: React.FC<LoginFooterProps> = ({
  variant,
  supportEmailId,
  handleSignUpClick,
  showSignUp,
  whatsappSignup,
  langData,
  domainConfig,
}) => {
  if (variant === "header") return null;

  if (variant === "mobilePage") {
    const whatsappLink = domainConfig?.suppportContacts?.find(
      (ct: { contactType?: string }) => ct.contactType === "REGISTRATION_WHATSAPP_LINK"
    )?.details;
    const apkUrl = getEnvVariable(domain, "apk_url") || domainConfig?.apkUrl;
    const downloadApp = () => {
      if (!apkUrl) return;
      trackApkDownload("login");
      const link = document.createElement("a");
      link.href = apkUrl.startsWith("http") ? apkUrl : "https://" + apkUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <>
        {/* Customer support - commented out per WhatsApp plan */}
        {/* <a
          href={`mailto:${supportEmailId}`}
          className="w-full mt-3 flex items-center justify-center gap-2 bg-[rgba(255,255,255,.6)] text-black4 rounded-lg py-2.5 px-3 text-sm font-medium border border-gray6"
        >
          <span className="text-xs">Customer support</span>
          <span className="text-xs">| support1 | support2</span>
        </a> */}
        {/* Placeholder WhatsApp row - replaced by single REGISTRATION_WHATSAPP_LINK button below */}
        {/* <a href="#" className="w-full flex ..."><img src={whatsappIcon} /> WhatsApp 3 | WhatsApp 4</a> */}
        {/* <div className="w-full border-t border-gray6 my-3" /> */}
        {/* <div className="flex w-full justify-center gap-2 flex-wrap"> Skype, Email, Instagram, Telegram </div> */}
        {/* <div className="w-full flex justify-center mt-3"><a href={`mailto:${supportEmailId}`}>...</a></div> */}

        <div className="flex flex-col gap-1 w-full mt-8">
        {apkUrl && (
          <div
            className="bg-[#3d8341] cursor-pointer w-full font-bold py-2 text-white flex justify-center items-center rounded gap-1"
            onClick={downloadApp}
          >
            <img src={androidIcon} alt="Android" className="w-6 h-6" />
            <span>Download .apk</span>
            <img src={downloadIconSvg} alt="download" className="w-4 h-4" />
          </div>
        )}

        <LoginBrandActions
          whatsappLink={whatsappLink || null}
          apkUrl={apkUrl || null}
        />
        </div>
        {showSignUp && (
          <div className="w-full flex justify-center mt-2">
            <span className="text-black4 text-sm">{langData?.["no_account"] ?? "Don't have an account?"}</span>
            <span onClick={handleSignUpClick} className="cursor-pointer text-black4 font-bold text-sm underline ml-1">
              {whatsappSignup ? langData?.["sign_in"] ?? "Sign In" : langData?.["sign_up"] ?? "Sign Up"}
            </span>
          </div>
        )}
      </>
    );
  }

  // standalone
  return (
    <>
      <div className="w-full flex justify-center">
        <a href={`mailto:${supportEmailId}`} className="text-white cursor-pointer text-xs uppercase">
          {supportEmailId}
        </a>
      </div>
      {showSignUp && (
        <div className="w-full flex justify-center">
          <div className="text-white">
            <span className="xl:hidden"> {langData?.["no_account"]}</span>
            <span onClick={handleSignUpClick} className="cursor-pointer text-[#ffff00] font-bold">
              {whatsappSignup ? langData?.["sign_in"] || "Sign In" : langData?.["sign_up"]}
            </span>
          </div>
        </div>
      )}
      <div className="border-t border-dashed border-white w-full p-1 flex justify-center relative">
        <div className="absolute left-0 w-[30px]">
          <div className="animate-slightLeftRightInfinite">
            <img alt="right-hand" className="w-8 object-cover" src={handIcon} />
          </div>
        </div>
        <div className="flex w-full justify-center">
          <a target="_blank" href="https://www.instagram.com/the.laser.official/" className="flex mx-4 items-center" rel="noreferrer">
            <img alt="instagram-icon" className="w-8 object-cover" src={instagramIcon} />
            <div className="text-white font-bold uppercase ml-1">{langData?.["instagram"] || "Instagram"}</div>
          </a>
          <a target="_blank" href="https://t.me/Laser247Tg" className="flex mx-4 items-center" rel="noreferrer">
            <img alt="telegram-icon" className="w-8 object-cover" src={telegramIcon} />
            <div className="text-white font-bold uppercase ml-1">{langData?.["telegram"]}</div>
          </a>
        </div>
      </div>
    </>
  );
};

// --- LoginSection (main) ---
interface LoginSectionProps {
  isHeader?: boolean;
  isMobileScreen?: boolean;
}

const LoginSection: React.FC<LoginSectionProps> = ({
  isMobileScreen = false,
  isHeader,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const goHomeReplace = () => navigate("/home", { replace: true });
  const { setSignUpModalOpen } = useSignUpModal();
  const { setResendEmailModalOpen } = useResendEmailModal();
  const { setForgotPasswordModalOpen } = useForgotPasswordModal();
  const { search } = useLocation();
  const authToken = new URLSearchParams(search).get("authToken");
  const { loggedIn } = useAppSelector((state) => state.auth);

  const variant: LoginVariant = isHeader ? "header" : isMobileScreen ? "mobilePage" : "standalone";

  const onSuccess = () => {
    const token = sessionStorage.getItem("jwt_token");
    if (token) {
      try {
        const claim = token.split(".")[1];
        const status = JSON.parse(window.atob(claim)).sts;
        if (status === 2) {
          navigate(ACCEPT_TERMS_ROUTE, { replace: true });
        } else if (status === 4) {
          navigate(RESET_PASSWORD_ROUTE, { replace: true });
        } else {
          dispatch(showToast({ type: "success", message: "Login Successfull" }));
        }
      } catch {
        // ignore
      }
    }
  };

  const loginReturn = useLogin({ navigate: goHomeReplace, onSuccess });
  const {
    formValues,
    handleChange,
    handleLoginSubmit,
    loading,
    onDemoLogin,
    domainConfig,
    langData,
    show2FAInput,
    setShow2FAInput,
    twoFactorCode,
    setTwoFactorCode,
    handle2FASubmit,
    is2FALoading,
    showReset2FA,
    setShowReset2FA,
    identifierValue,
    setIdentifierValue,
    resetIdentifierType,
    setResetIdentifierType,
    resetOtp,
    setResetOtp,
    resetOtpTimer,
    resetOtpEnterFields,
    resetLoading,
    isResetting,
    resetErrorMsg,
    generateResetOtp,
    resetTwoFactor,
    handleReset2FABack,
  } = loginReturn;
  const handle2FABack =
    typeof (loginReturn as unknown as { handle2FABack?: () => void }).handle2FABack === "function"
      ? (loginReturn as unknown as { handle2FABack: () => void }).handle2FABack
      : () => {
          setShow2FAInput(false);
          setTwoFactorCode("");
        };

  const [validationCode, setValidationCode] = useState(() =>
    String(Math.floor(1000 + Math.random() * 9000))
  );
  const [validationInput, setValidationInput] = useState("");
  const [demoLoginLoader, setDemoLoginLoader] = useState(false);

  const setSapToken = async (token: string) => {
    await getSapToken(token);
  };

  useEffect(() => {
    if (authToken) {
      setSapToken(authToken);
      const username = authToken?.split(".")?.[1];
      const uname = JSON.parse(window?.atob(username)).sub;
      const uid = JSON.parse(window?.atob(username)).uid;
      sessionStorage.setItem("username", uname);
      sessionStorage.setItem("aid", uid);
      sessionStorage.setItem("jwt_token", authToken);
      const claim = authToken.split(".")[1];
      sessionStorage.setItem("aid", JSON.parse(window.atob(claim)).aid);
      dispatch(loginSuccess(authToken));
      goHomeReplace();
    }
  }, [authToken, dispatch, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setValidationCode(String(Math.floor(1000 + Math.random() * 9000)));
    }, 20_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (loggedIn) {
      setDemoLoginLoader(false);
      try {
        const claim = sessionStorage.getItem("jwt_token")?.split(".")[1];
        if (claim) {
          const status = JSON.parse(window.atob(claim)).sts;
          if (status === 2) navigate(ACCEPT_TERMS_ROUTE, { replace: true });
          else if (status === 4) navigate(RESET_PASSWORD_ROUTE, { replace: true });
        }
      } catch {
        // ignore
      }
    }
  }, [loggedIn, navigate]);

  const handleLogin = () => {
    if (validationInput.trim() !== validationCode) {
      dispatch(
        showToast({
          type: "error",
          message:
            langData?.["validation_code_mismatch"] ??
            "Validation code does not match. Please enter the displayed number.",
        })
      );
      return;
    }
    handleLoginSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  const handleForgotPassClick = () => setForgotPasswordModalOpen(true);
  const handleResendEmailClick = () => setResendEmailModalOpen(true);
  // HA signup off + signin on => Sign Up opens the registration WhatsApp link instead of the sign-up page.
  const registrationWhatsappLink = domainConfig?.suppportContacts?.find(
    (ct) => ct.contactType === "REGISTRATION_WHATSAPP_LINK"
  )?.details;
  const whatsappSignup = isWhatsappSignup(domainConfig);
  const showSignUp = whatsappSignup
    ? !!registrationWhatsappLink
    : !!(domainConfig?.signup && domainConfig?.b2cEnabled);

  // Login page must navigate away so Sign Up doesn't stack over it (desktop + mobile).
  // Header on desktop can open the modal over the current page.
  const handleSignUpClick = () => {
    if (whatsappSignup) {
      window.open(registrationWhatsappLink, "_blank");
      return;
    }
    if (isMobile || variant === "mobilePage") {
      navigate(SIGN_UP_ROUTE);
      return;
    }
    setSignUpModalOpen(true);
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleLogin();
  };

  useEffect(() => {
    if (variant !== "header" || !show2FAInput) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handle2FABack();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [variant, show2FAInput, handle2FABack]);

  const twoFAContentProps = {
    showReset2FA,
    setShowReset2FA,
    twoFactorCode,
    setTwoFactorCode,
    handle2FASubmit,
    handle2FABack,
    is2FALoading,
    identifierValue,
    setIdentifierValue,
    resetIdentifierType,
    setResetIdentifierType,
    resetOtp,
    setResetOtp,
    resetOtpTimer,
    resetOtpEnterFields,
    resetLoading,
    isResetting,
    resetErrorMsg,
    generateResetOtp,
    resetTwoFactor,
    handleReset2FABack,
    langData,
  };

  // Mobile: inline 2FA when on login page and 2FA required
  if (variant === "mobilePage" && show2FAInput) {
    return (
      <div className="flex flex-col w-full space-y-2 text-black3">
        <div className="w-full bg-white rounded-lg p-4 shadow border border-gray-200">
          <Login2FAContent {...twoFAContentProps} isInline={true} />
        </div>
      </div>
    );
  }

  const loginForm = (
    <div
      className={`items-center ${
        variant === "mobilePage"
          ? "flex flex-col w-full space-y-2 text-black3"
          : "hidden md:flex max-md:space-y-2 w-auto ml-1"
      }`}
    >
      <LoginFormFields
        variant={variant}
        formValues={formValues}
        handleChange={handleChange}
        validationCode={validationCode}
        validationInput={validationInput}
        setValidationInput={setValidationInput}
        handleEnterKeyPress={handleEnterKeyPress}
        handleForgotPassClick={handleForgotPassClick}
        handleResendEmailClick={handleResendEmailClick}
        handleSignUpClick={handleSignUpClick}
        showSignUp={showSignUp}
        whatsappSignup={whatsappSignup}
        langData={langData}
        domainConfig={domainConfig}
      />
      <LoginActionButtons
        variant={variant}
        handleLogin={handleLogin}
        onDemoLogin={onDemoLogin}
        loading={loading}
        demoLoginLoader={demoLoginLoader}
        setDemoLoginLoader={setDemoLoginLoader}
        langData={langData}
        domainConfig={domainConfig}
      />
      <LoginFooter
        variant={variant}
        supportEmailId={supportEmailId}
        handleSignUpClick={handleSignUpClick}
        showSignUp={showSignUp}
        whatsappSignup={whatsappSignup}
        langData={langData}
        domainConfig={domainConfig}
      />
    </div>
  );

  // Desktop: show login row and overlay 2FA popup when 2FA required
  if (variant === "header") {
    return (
      <>
        {loginForm}
        {show2FAInput && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={langData?.["two_factor_auth"] ?? "Two-Factor Authentication"}
          >
            <div
              className="relative w-full max-w-md rounded-lg bg-black p-6 shadow-xl border border-goldenYellow"
              onClick={(e) => e.stopPropagation()}
            >
              <Login2FAContent {...twoFAContentProps} isInline={false} />
            </div>
          </div>
        )}
      </>
    );
  }

  return loginForm;
};

export default LoginSection;
